/* eslint-disable import/prefer-default-export */
import { ApolloCache, FieldFunctionOptions, FieldPolicy, Reference } from '@apollo/client'
import { Cache } from '@apollo/client/cache/core/types/Cache'
import { KeySpecifier } from '@apollo/client/cache/inmemory/policies'
import uniqBy from 'lodash.uniqby'

import { sortReferences } from '~apollo/helpers'
import { PaginatedGroup } from '~generated/types'
import { isProduction } from '~lib/helpers'

export enum MergeStrategy {
  ADD_DOCS = 'ADD_DOCS',
  UPDATE_DOCS = 'UPDATE_DOCS',
  DELETE_DOCS = 'DELETE_DOCS',
}

type Incoming = {
  docs: Reference[]
  groups: PaginatedGroup[]
  totalPages: number
  page: number
}

type Existing = Incoming | undefined

type SortInputType<G, S> = { groupBy: G; groupsSort: S; docsSort: S }

export interface PaginatedGroupWithDocs<Doc extends any = any> extends Omit<PaginatedGroup, 'count'> {
  docs: Doc[]
}

export type GroupsSortComparer = (a: PaginatedGroupWithDocs, b: PaginatedGroupWithDocs, readField) => number
export type DocsSortComparer = (a: Reference, b: Reference, readField) => number

export type PaginatedGroupsFieldPolicyOptions<
  GroupByKey extends string = string,
  TasksDocsSortKey extends string = string
> = {
  getGroupKeysFromDocRef: (ref: Reference, sort: SortInputType<GroupByKey, TasksDocsSortKey>, readField) => string[]
  groupsSortComparers: Record<TasksDocsSortKey, GroupsSortComparer>
  docsSortComparers: Record<TasksDocsSortKey, DocsSortComparer>
}
const sortKeyArgs = ['sort', ['groupBy', 'docsSort', 'groupValues']]

export const OTHER_GROUP_KEY = 'OTHER'

export class PaginatedGroupsFieldPolicy<GroupByKey extends string = string, TasksDocsSortKey extends string = string>
  implements FieldPolicy
{
  // TODO create logging service
  private readonly debugEnabled = !isProduction()

  static cropGroups(groups: PaginatedGroup[], docsCount: number) {
    let cursor = 0
    return groups.filter((group) => {
      const match = cursor <= docsCount
      cursor += group.count
      return match
    })
  }

  static sortGroups(groups: PaginatedGroupWithDocs[], comparer: GroupsSortComparer, readField) {
    return [...groups].sort((a, b) => comparer(a, b, readField))
  }

  static updateQuery<TData = any, TVariables extends { mergeStrategy: MergeStrategy } = any>(
    cache: ApolloCache<any>,
    options: Cache.UpdateQueryOptions<TData, TVariables>,
    update: (data: TData | null) => TData | null | void
  ) {
    cache.updateQuery<TData, TVariables>(options, update)
  }

  public readonly keyArgs: KeySpecifier

  // private readonly options: PaginatedGroupsFieldPolicyOptions<GroupByKey, TasksDocsSortKey>

  constructor(
    keyArgs: KeySpecifier | false = false,
    private readonly options: PaginatedGroupsFieldPolicyOptions<GroupByKey, TasksDocsSortKey>
  ) {
    this.keyArgs = [...(keyArgs || []), ...sortKeyArgs]
    this.options = options
  }

  private debug(...params) {
    if (!this.debugEnabled) return
    // eslint-disable-next-line no-console
    console.debug(...params)
  }

  getGroupKeysFromDocRef(ref: Reference, sort: SortInputType<GroupByKey, TasksDocsSortKey>, readField): string[] {
    return this.options.getGroupKeysFromDocRef(ref, sort, readField)
  }

  private getGroupsSortComparer(sort: SortInputType<GroupByKey, TasksDocsSortKey>) {
    return this.options.groupsSortComparers[sort.groupsSort]
  }

  private getDocsSortComparer(sort: SortInputType<GroupByKey, TasksDocsSortKey>) {
    return this.options.docsSortComparers[sort.docsSort]
  }

  merge = (existing: Existing, incoming: Incoming, { readField, variables }: FieldFunctionOptions) => {
    this.debug(
      'PaginatedGroupsFieldPolicy.merge',
      '\nexisting: ',
      existing,
      '\nincoming:',
      incoming,
      '\nvariables:',
      variables
    )
    const { sort, pagination, mergeStrategy } = variables
    const limit = pagination?.limit || 10
    const { page } = incoming
    const calledAfterFirstRender = !mergeStrategy && page === 1 // needs to reset cache because data may be out of date

    if (!existing || calledAfterFirstRender) {
      this.debug('PaginatedGroupsFieldPolicy.merge -> done', '\nresult:', incoming)
      return {
        ...incoming,
        docs: incoming.docs || [],
        groups: incoming.groups
          ? PaginatedGroupsFieldPolicy.cropGroups([...incoming.groups], incoming.docs.length)
          : [],
      }
    }

    let uniqueDocs

    if (mergeStrategy === MergeStrategy.DELETE_DOCS) {
      const docsIdsToRemove = new Set(incoming.docs.map((ref) => readField('id', ref)))
      uniqueDocs = uniqBy(existing.docs, (e) => readField('id', e)).filter(
        (ref) => !docsIdsToRemove.has(readField('id', ref))
      )
    } else {
      uniqueDocs = uniqBy([...existing.docs, ...incoming.docs], (e) => readField('id', e))
    }
    this.debug('uniqueDocs:', uniqueDocs)

    const sortDocsComparer =
      this.getDocsSortComparer(sort) || ((a, b, readField) => readField('name', a).localeCompare(readField('name', b)))

    // TODO create separate variable for grouping and update query
    if (!sort.groupBy || !incoming.groups) {
      // grouping is not used
      const result = {
        ...existing,
        ...incoming,
        docs: sortReferences(uniqueDocs, sortDocsComparer, readField),
        groups: [],
      }
      this.debug('PaginatedGroupsFieldPolicy.merge -> done', '\nresult:', result)
      return result
    }

    const otherGroup = { key: OTHER_GROUP_KEY, docs: [] }

    const groupsWithDocsMap = new Map()

    uniqueDocs.forEach((doc) => {
      let groupKeys = this.getGroupKeysFromDocRef(doc, sort, readField)
      // for now groupValues works only with tags
      const shouldShowSelectedGroups = sort?.groupValues?.length > 0

      if (shouldShowSelectedGroups) {
        groupKeys = groupKeys.filter((key) => sort.groupValues.includes(key))
      }

      if (groupKeys.length === 0) {
        otherGroup.docs.push(doc)
        return
      }

      groupKeys.forEach((groupKey) => {
        const newDocs = [...(groupsWithDocsMap.get(groupKey)?.docs || []), doc]
        groupsWithDocsMap.set(groupKey, {
          key: groupKey,
          count: newDocs.length,
          docs: newDocs,
        })
      })
    })

    const sortDocsByGroupsSortComparer = this.getDocsSortComparer({ ...sort, docsSort: sort.groupsSort }) // sort by groupsSort param

    const sortedGroupsWithDocs = PaginatedGroupsFieldPolicy.sortGroups(
      [...groupsWithDocsMap.values()].map((group) => {
        return {
          key: group.key,
          docs: sortReferences(group.docs, sortDocsByGroupsSortComparer, readField),
        }
      }),
      this.getGroupsSortComparer(sort),
      readField
    )

    const { sortedDocs, sortedGroups } = (
      otherGroup.docs.length > 0 ? [...sortedGroupsWithDocs, otherGroup] : sortedGroupsWithDocs
    ).reduce(
      (acc, { docs, key }) => {
        const sortedDocs =
          sort.docsSort === sort.groupsSort && // don't resort if order is the same
          key !== OTHER_GROUP_KEY
            ? docs
            : sortReferences(docs, sortDocsComparer, readField)
        return {
          sortedDocs: [...acc.sortedDocs, ...sortedDocs],
          sortedGroups: [...acc.sortedGroups, { key, count: docs.length }],
        }
      },
      {
        sortedDocs: [],
        sortedGroups: [],
      }
    )

    const calledAfterFetchMore = !mergeStrategy

    if (calledAfterFetchMore) {
      const docs = sortedDocs.slice(0, page * limit)
      const groups = PaginatedGroupsFieldPolicy.cropGroups(sortedGroups, docs.length)

      const result = {
        ...existing,
        ...incoming,
        // FIXME currently backend just increments page variable :C
        nextPage: incoming.totalPages > page ? page + 1 : null,
        docs,
        groups,
      }

      this.debug('PaginatedGroupsFieldPolicy.merge -> done', '\nresult:', result)
      return result
    }

    let newPage = page
    const currentMaxDocs = page * limit

    // happens when user deleted tasks
    const hasPagesWithMissingDocuments = currentMaxDocs > sortedDocs.length

    if (hasPagesWithMissingDocuments) {
      newPage = Math.ceil(sortedDocs.length / limit)
    }

    const docs = sortedDocs.slice(0, newPage * limit)

    const groups = PaginatedGroupsFieldPolicy.cropGroups(sortedGroups, docs.length)

    this.debug({ page, newPage, limit, groups, docs, sortedGroups })

    const totalPages = newPage > existing.totalPages ? newPage + 1 : existing.totalPages

    const result = {
      ...existing,
      ...incoming,
      page: newPage,
      nextPage: totalPages > newPage ? newPage + 1 : null,
      totalPages,
      docs,
      groups,
    }

    this.debug('PaginatedGroupsFieldPolicy.merge -> done', '\nresult:', result)
    return result
  }
}
